import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { assign as radashAssign } from 'radash';

import { BaseChartComponent } from '@connect-services/client/components/statistics/base-chart/base-chart.component';
import { ChartLayoutComponent } from '@connect-services/client/components/statistics/chart-layout/chart-layout.component';
import {
    BaseChartOptions,
    ChartComponentInterface,
    ChartService,
    collapsedColumnHeightChart,
    commonOptions,
    expandedColumnHeightChart,
    NO_LABEL,
    Series,
    SeriesFormated,
} from '@connect-services/client/services/chart.service';
import {
    ComputedStatistic,
    LineColumnComputedStatisticResult,
    StatisticRenderOptions,
} from '@connect-services/src/sdk/api';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BaseChartComponent, ChartLayoutComponent],
    selector: 'app-column-chart',
    styleUrl: './column-chart.component.scss',
    templateUrl: './column-chart.component.html',
})
export class ColumnChartComponent implements ChartComponentInterface {
    public readonly computedStatistic = input.required<ComputedStatistic>();
    public readonly hoverNameInformation = signal<string>('');
    public readonly hoverSubtitleInformation = signal<string>('');
    public readonly hoverTitleInformation = signal<string>('');
    public readonly hoverTitleInformationColor = signal<string>('#2f2f2f');
    public readonly isExpanded = input.required<boolean>();
    public readonly isPrintScreen = input<boolean>(false);
    public readonly options = computed<BaseChartOptions>(() => {
        const computedStatistic = this.computedStatistic();
        const renderOptions = computedStatistic.renderOptions;

        if (!computedStatistic.renderOptions) {
            return {} as BaseChartOptions;
        }

        const series = this.chartService.setSeries(computedStatistic);
        const categories = this.chartService.getCategories(
            this.computedStatistic().result!.data as LineColumnComputedStatisticResult[],
        );
        const alignSeries: SeriesFormated[] = this.chartService.alignMultiplesSeries(series as Series, categories);

        const nbCategories = categories.length;

        let columnWidth = '70%';

        if (nbCategories === 1) {
            columnWidth = '10%';
        }

        if (nbCategories > 1 && nbCategories <= 5) {
            columnWidth = '30%';
        }
        if (nbCategories > 5 && nbCategories <= 10) {
            columnWidth = '50%';
        }

        return radashAssign(commonOptions, {
            chart: {
                animations: {
                    enabled: !this.isPrintScreen(),
                },
                events: {
                    dataPointMouseEnter: (_event, _chartContext, opts) => {
                        const dataValue = opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex];
                        const category = opts.w.config.xaxis.categories[opts.dataPointIndex];

                        this.hoverTitleInformation.set(`${dataValue} ${computedStatistic.renderOptions.unit?.y ?? ''}`);
                        this.hoverSubtitleInformation.set(`${category}`);
                        this.hoverTitleInformationColor.set(opts.w.config.fill.colors[opts.seriesIndex]);
                        const name = opts.w.config.series[opts.seriesIndex].name;
                        this.hoverNameInformation.set(name === NO_LABEL ? undefined : name);
                    },
                    mounted: () => {
                        this.ready.emit();
                    },
                    mouseLeave: () => {
                        this.hoverTitleInformation.set('');
                        this.hoverSubtitleInformation.set('');
                    },
                },
                height: this.isExpanded() ? expandedColumnHeightChart : collapsedColumnHeightChart,
                toolbar: {
                    show: false,
                },
                type: 'bar',
            },
            dataLabels: {
                enabled: this.isPrintScreen(),
            },
            fill: {
                colors: renderOptions.colors ?? [],
            },
            legend: {
                horizontalAlign: 'left',
                labels: {
                    colors: renderOptions.colors ?? [],
                },
                markers: {
                    fillColors: renderOptions.colors ?? [],
                },
                offsetY: 8,
                position: renderOptions.legend?.position,
            },
            plotOptions: {
                bar: {
                    borderRadius: 5,
                    columnWidth,
                    horizontal: renderOptions.orientation === StatisticRenderOptions.OrientationEnum.HORIZONTAL,
                },
            },
            series: alignSeries
                ? alignSeries.map((alignSerie) => {
                      return {
                          data: alignSerie.data as (number | null)[],
                          name: alignSerie.name,
                      };
                  })
                : undefined,
            states: {
                hover: {
                    filter: {
                        type: 'darken',
                    },
                },
            },
            tooltip: {
                enabled: false,
            },
            xaxis: {
                categories: categories,
                labels: {
                    style: {
                        fontSize: '11px',
                    },
                },
                title: {
                    offsetX: 0,
                    offsetY: -5,
                    text: renderOptions.xaxisTitle?.show
                        ? this.chartService.formatAxisText(
                              renderOptions.xaxisTitle?.text ?? '',
                              renderOptions.unit?.x ?? '',
                          )
                        : '',
                },
            },
            yaxis: {
                show: this.isPrintScreen() ? this.isPrintScreen() : renderOptions.yaxisTitle?.show,
                title: {
                    text: this.chartService.formatAxisText(renderOptions.yaxisTitle?.text, renderOptions.unit?.y),
                },
            },
        });
    });

    public ready = output<void>();

    protected chartService = inject(ChartService);
}
